import React, {
  useCallback, useEffect, useMemo, useRef, useState
} from 'react';
import axios from 'axios';
import IstClock from './IstClock';
import './FutureTrading.css';

const API_BASE = '/api/future-trading';
const POLL_MS = 15000;
const DEFAULT_CAPITAL = 10000;
const DEFAULT_TARGET_RS = 1.00;

function round2(n) { return Math.round((Number(n) || 0) * 100) / 100; }
function fmt(n, d = 2) {
  const v = round2(n);
  return v >= 0 ? `+₹${v.toFixed(d)}` : `−₹${Math.abs(v).toFixed(d)}`;
}
function fmtPlain(n, d = 2) { return `₹${round2(n).toFixed(d)}`; }
function pct(n) { return `${Number(n) >= 0 ? '+' : ''}${Number(n).toFixed(2)}%`; }

// ── Zerodha Intraday charge estimate (frontend mirror) ─────────────────────────
function estimateCharges(buyPx, sellPx, qty) {
  const tBuy = buyPx * qty;
  const tSell = sellPx * qty;
  const br = Math.min(20, tBuy * 0.0003) + Math.min(20, tSell * 0.0003);
  const stt = Math.round(tSell * 0.00025);
  const txn = (tBuy + tSell) * 0.0000307;
  const sebi = (tBuy + tSell) * 0.000001;
  const stamp = Math.round(tBuy * 0.00003);
  const gst = 0.18 * (br + txn + sebi);
  return round2(br + stt + txn + sebi + stamp + gst);
}

function calcNetProfit(entryPx, exitPx, qty) {
  const gross = round2((exitPx - entryPx) * qty);
  const charges = estimateCharges(entryPx, exitPx, qty);
  return { gross, charges, net: round2(gross - charges) };
}

// ── BUY Modal ─────────────────────────────────────────────────────────────────
function BuyModal({ row, capital, profitTargetRs, onClose, onBuy }) {
  const maxQty = Math.max(1, Math.floor(capital / (row?.price || 1)));
  const [qty, setQty] = useState(String(maxQty));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const q = Math.max(1, parseInt(qty, 10) || 1);
  const preview = useMemo(() => {
    if (!row?.price) return null;
    const exitPx = round2(Number(row.price) + profitTargetRs);
    return calcNetProfit(Number(row.price), exitPx, q);
  }, [row?.price, profitTargetRs, q]);

  async function handleBuy() {
    setBusy(true);
    setError('');
    try {
      const res = await axios.post(`${API_BASE}/buy`, {
        symbol: row.symbol,
        stock: row.stock,
        price: row.price,
        qty: q,
        exchange: row.exchange || 'NSE'
      });
      if (!res.data?.ok) throw new Error(res.data?.error || 'Buy failed');
      onBuy(res.data);
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Buy failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="ft-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="ft-modal">
        <h3>📥 Buy {row?.stock}</h3>

        <div className="ft-modal-field">
          <label>Stock</label>
          <input value={`${row?.stock} — ₹${row?.price}`} readOnly />
        </div>
        <div className="ft-modal-field">
          <label>Quantity (max {maxQty} from ₹{capital})</label>
          <input
            type="number" min="1" max={maxQty}
            value={qty}
            onChange={e => setQty(e.target.value)}
          />
        </div>

        {preview && (
          <div className="ft-charges-preview">
            <div>Buy turnover: <strong>{fmtPlain(Number(row.price) * q)}</strong></div>
            <div>Target exit @ <strong>{fmtPlain(Number(row.price) + profitTargetRs)}</strong> (+₹{profitTargetRs}/sh)</div>
            <div>Zerodha intraday charges: <strong>−₹{preview.charges.toFixed(2)}</strong></div>
            <div>Gross profit: <strong style={{ color: preview.gross > 0 ? '#4ade80' : '#f87171' }}>{fmt(preview.gross)}</strong></div>
            <div style={{ fontWeight: 800, marginTop: 4 }}>
              Net profit: <span className={preview.net > 0 ? 'ft-charges-net pos' : 'ft-charges-net neg'}>
                {preview.net > 0 ? `+₹${preview.net.toFixed(2)}` : `−₹${Math.abs(preview.net).toFixed(2)}`}
              </span>
              {preview.net <= 0 && <span style={{ fontSize: '0.68rem', marginLeft: 6, color: '#f87171' }}>⚠ Will be blocked at sell</span>}
            </div>
          </div>
        )}

        {error && <div style={{ color: '#f87171', fontSize: '0.75rem', marginBottom: 10 }}>{error}</div>}

        <div className="ft-modal-actions">
          <button className="ft-btn primary" onClick={handleBuy} disabled={busy}>
            {busy ? 'Buying…' : 'Confirm BUY'}
          </button>
          <button className="ft-btn" style={{ background: '#374151', color: '#e2e8f0' }} onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Screen ────────────────────────────────────────────────────────────────
export default function FutureTrading() {
  const [board, setBoard] = useState(null);
  const [capital, setCapital] = useState(DEFAULT_CAPITAL);
  const [profitTargetRs, setProfitTargetRs] = useState(DEFAULT_TARGET_RS);
  const [capitalInput, setCapitalInput] = useState(String(DEFAULT_CAPITAL));
  const [targetInput, setTargetInput] = useState(String(DEFAULT_TARGET_RS));
  const [scanning, setScanning] = useState(false);
  const [buyRow, setBuyRow] = useState(null);
  const [toast, setToast] = useState('');
  const [sellBusy, setSellBusy] = useState({});
  const [automBusy, setAutomBusy] = useState(false);
  const [tradeAlert, setTradeAlert] = useState(null);
  const [activeTab, setActiveTab] = useState('buy'); // buy | hold | sell | sold
  const seenAlertRef = useRef(new Set());
  const timerRef = useRef(null);

  const showToast = useCallback((msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3500);
  }, []);

  const loadBoard = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/board`, { timeout: 20000 });
      if (res.data?.ok !== false) {
        setBoard(res.data);
        if (res.data.capital != null) {
          setCapital(res.data.capital);
          setCapitalInput(String(res.data.capital));
        }
        if (res.data.profitTargetRs != null) {
          setProfitTargetRs(res.data.profitTargetRs);
          setTargetInput(String(res.data.profitTargetRs));
        }
      }
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    loadBoard();
    timerRef.current = setInterval(loadBoard, POLL_MS);
    return () => clearInterval(timerRef.current);
  }, [loadBoard]);

  // Browser + on-screen alerts when bot buys or takes profit
  useEffect(() => {
    const alert = board?.lastAlert;
    if (!alert?.alert || !alert.ts) return;
    const key = `${alert.ts}-${alert.type}-${alert.stock}`;
    if (seenAlertRef.current.has(key)) return;
    seenAlertRef.current.add(key);
    if (seenAlertRef.current.size > 80) {
      seenAlertRef.current = new Set([...seenAlertRef.current].slice(-40));
    }

    setTradeAlert(alert);
    showToast(alert.msg || alert.message);

    if (typeof window !== 'undefined' && 'Notification' in window) {
      const body = alert.hindi || alert.msg || alert.message;
      const title = alert.type === 'trade_close'
        ? 'Future · Profit sell'
        : 'Future · BUY this share';
      if (Notification.permission === 'granted') {
        new Notification(title, { body, tag: `ft-${alert.type}-${alert.stock}` });
      } else if (Notification.permission !== 'denied') {
        Notification.requestPermission().then((perm) => {
          if (perm === 'granted') new Notification(title, { body, tag: `ft-${alert.type}-${alert.stock}` });
        });
      }
    }

    try {
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        const u = new SpeechSynthesisUtterance(
          alert.type === 'trade_close'
            ? `Profit. Sold ${alert.stock}. Net ${alert.net} rupees.`
            : `Buy alert. Bought ${alert.stock}.`
        );
        u.lang = 'en-IN';
        u.rate = 1;
        window.speechSynthesis.cancel();
        window.speechSynthesis.speak(u);
      }
    } catch { /* speech optional */ }
  }, [board?.lastAlert, showToast]);

  async function handleScan() {
    setScanning(true);
    try {
      const res = await axios.post(`${API_BASE}/scan`, {}, { timeout: 35000 });
      if (res.data?.ok !== false) setBoard(res.data);
      showToast(`✅ Scan complete — ${res.data?.filteredList?.length ?? 0} stocks passed ML factors`);
    } catch (e) {
      showToast(`Scan failed: ${e.message}`);
    } finally {
      setScanning(false);
    }
  }

  async function handleSettings() {
    const cap = parseFloat(capitalInput);
    const tgt = parseFloat(targetInput);
    if (!cap || cap < 100) return showToast('Capital must be ≥ ₹100');
    if (!tgt || tgt <= 0) return showToast('Profit target must be > 0');
    try {
      const res = await axios.post(`${API_BASE}/settings`, { capital: cap, profitTargetRs: tgt });
      if (res.data?.ok) {
        setCapital(res.data.capital);
        setProfitTargetRs(res.data.profitTargetRs);
        showToast(`Settings updated — Capital ₹${res.data.capital} · Target +₹${res.data.profitTargetRs}/sh`);
        loadBoard();
      }
    } catch (e) {
      showToast(`Settings error: ${e.message}`);
    }
  }

  async function handleSell(pos) {
    if (!pos?.canSell) {
      showToast(`❌ ${pos.stock}: Net ≤ 0 — blocked. Current net: ₹${pos.netPnl}`);
      return;
    }
    setSellBusy(b => ({ ...b, [pos.id]: true }));
    try {
      const res = await axios.post(`${API_BASE}/sell`, {
        positionId: pos.id,
        stock: pos.stock,
        price: pos.currentPrice
      });
      if (res.data?.ok) {
        showToast(`✅ SOLD ${pos.stock} · Net ₹${res.data.net}`);
        loadBoard();
      } else {
        showToast(`❌ ${res.data?.error || 'Sell failed'}`);
      }
    } catch (e) {
      showToast(`Sell error: ${e.message}`);
    } finally {
      setSellBusy(b => ({ ...b, [pos.id]: false }));
    }
  }

  async function handleAutomation() {
    setAutomBusy(true);
    try {
      const on = !(board?.automationOn ?? true);
      const res = await axios.post(`${API_BASE}/automation`, { on });
      if (res.data?.ok) {
        showToast(`🤖 Automation ${res.data.automationOn ? 'ENABLED — bot will auto buy/sell' : 'PAUSED — manual only'}`);
        loadBoard();
      }
    } catch (e) {
      showToast(`Error: ${e.message}`);
    } finally {
      setAutomBusy(false);
    }
  }

  const filteredList = board?.filteredList || [];
  const positions = board?.positions || [];
  const trades = board?.trades || [];
  const log = board?.activityLog || [];
  const marketOpen = board?.marketOpen;
  const dailyPnl = round2(board?.dailyPnl || 0);
  const totalPnl = round2(board?.totalPnl || 0);
  const niftyChange = board?.niftyChange;
  const automationOn = board?.automationOn ?? true;
  const winRate = board?.winRate;
  const totalTrades = board?.totalTrades || 0;
  const winTrades = board?.winTrades || 0;

  const actionLists = board?.actionLists || {};
  const buyTab = actionLists.buy?.length
    ? actionLists.buy
    : filteredList.filter((r) => r.canAutoBuy && !positions.some((p) => p.stock === r.stock && !p.sold)).slice(0, 25);
  const holdTab = actionLists.hold?.length
    ? actionLists.hold
    : positions.filter((p) => !p.sold && !p.canSell);
  const sellTab = actionLists.sell?.length
    ? actionLists.sell
    : positions.filter((p) => !p.sold && p.canSell);
  const soldTab = actionLists.soldToday?.length ? actionLists.soldToday : trades;

  function renderPosCard(pos) {
    const cur = pos.currentPrice != null ? Number(pos.currentPrice) : Number(pos.entryPrice);
    const live = calcNetProfit(pos.entryPrice, cur, pos.qty);
    // Prefer live calc; if LTP missing, keep last persisted server net (never invent 0)
    const net = pos.currentPrice != null
      ? live.net
      : (pos.netPnl != null ? round2(pos.netPnl) : live.net);
    const charges = live.charges;
    const gross = pos.currentPrice != null ? live.gross : round2((cur - pos.entryPrice) * pos.qty);
    const canSell = net > 0 || !!pos.canSell;
    const needPerShare = canSell
      ? 0
      : Math.max(0.01, round2((charges / pos.qty) - (cur - pos.entryPrice) + 0.01));
    const holdMins = pos.openedAt
      ? Math.max(0, Math.floor((Date.now() - new Date(pos.openedAt).getTime()) / 60000))
      : null;
    const holdText = holdMins == null
      ? ''
      : holdMins < 60
        ? `${holdMins}m`
        : `${Math.floor(holdMins / 60)}h ${holdMins % 60}m`;
    const statusLabel = canSell
      ? 'SELL · net profit — sell allowed / auto-sell armed'
      : net < 0
        ? `HOLD · unrealized ${fmt(net)}`
        : 'HOLD · charges not covered yet';
    return (
      <div key={pos.id} className={`ft-pos-card${canSell ? ' can-sell' : ' waiting'}`}>
        <div className={`ft-pos-status ${canSell ? 'ready' : 'waiting'}`}>
          {pos.workflowReason || statusLabel}
          {!canSell && (
            <span className="ft-pos-need"> · need ~+₹{needPerShare.toFixed(2)}/sh more</span>
          )}
          {holdText && <span className="ft-pos-hold"> · held {holdText}</span>}
        </div>
        <div className="ft-pos-top">
          <span className="ft-pos-stock">{pos.stock}
            {pos.autoEntry && <span style={{ fontSize: '0.6rem', background: '#1d4ed8', color: '#bfdbfe', borderRadius: 4, padding: '1px 5px', marginLeft: 5 }}>🤖 AUTO</span>}
          </span>
          <span className="ft-pos-meta">
            <span>Entry ₹{pos.entryPrice}</span>
            <span>Qty {pos.qty}</span>
            <span>Capital ₹{round2(pos.entryPrice * pos.qty).toFixed(0)}</span>
            <span>Exchange {pos.exchange}</span>
          </span>
          <span className={`ft-pos-move ${(pos.move ?? 0) >= 0 ? 'pos' : 'neg'}`}>
            {(pos.move ?? 0) >= 0 ? '+' : ''}₹{round2(pos.move ?? 0).toFixed(2)}/sh
          </span>
          <div className="ft-pos-sell-btn">
            {canSell ? (
              <button
                className="ft-btn success small"
                disabled={sellBusy[pos.id]}
                onClick={() => handleSell({ ...pos, canSell, netPnl: net })}
              >
                {sellBusy[pos.id] ? 'Selling…' : `SELL +₹${net.toFixed(2)} net`}
              </button>
            ) : (
              <span className="ft-sell-block">🔒 Sell blocked until net &gt; 0</span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: '0.71rem', color: '#94a3b8' }}>
          <span>Current ₹{cur.toFixed(2)}</span>
          <span>Gross {fmt(gross)}</span>
          <span>Charges ≈ ₹{charges.toFixed(2)}</span>
          <span style={{ color: canSell ? '#4ade80' : '#f87171', fontWeight: 700 }}>
            Unrealized net {fmt(net)}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="ft-root">
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 72, left: '50%', transform: 'translateX(-50%)',
          background: '#1e293b', border: '1px solid #3b82f6', borderRadius: 10,
          padding: '10px 18px', zIndex: 2000, fontSize: '0.82rem', maxWidth: 480,
          textAlign: 'center', boxShadow: '0 8px 24px rgba(0,0,0,0.5)'
        }}>
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="ft-header">
        <div className="ft-header-titles">
          <h1>📊 Future Trading</h1>
          <p>
            ALL Indian NSE/BSE stocks → ML factors → ₹{capital.toLocaleString()} capital →
            Zerodha Intraday charges → Sell only when net profit &gt; 0
          </p>
        </div>
        <div className="ft-header-meta">
          <span className={`ft-pill ${marketOpen ? 'live' : 'closed'}`}>
            {marketOpen && <span className="ft-pill-dot" />}
            {marketOpen ? 'MARKET LIVE' : 'MARKET CLOSED'}
          </span>
          {niftyChange != null && (
            <span className={`ft-pill ${niftyChange >= 0 ? 'live' : 'closed'}`} style={{ fontSize: '0.68rem' }}>
              NIFTY {pct(niftyChange)}
            </span>
          )}
          {board?.sensexChange != null && (
            <span className={`ft-pill ${board.sensexChange >= 0 ? 'live' : 'closed'}`} style={{ fontSize: '0.68rem' }}>
              SENSEX {pct(board.sensexChange)}
            </span>
          )}
          <span className="ft-pill" style={{ fontSize: '0.68rem', background: 'rgba(59,130,246,0.15)', borderColor: 'rgba(59,130,246,0.4)', color: '#93c5fd' }}>
            India NSE+BSE only
          </span>
          <IstClock />
        </div>
      </div>

      {/* Live trade alert — BUY this share / profit sell */}
      {(tradeAlert || board?.lastAlert?.alert) && (
        <div
          className={`ft-trade-alert ${(tradeAlert || board.lastAlert).type === 'trade_close' ? 'profit' : 'buy'}`}
          role="alert"
        >
          <div className="ft-trade-alert-title">
            {(tradeAlert || board.lastAlert).type === 'trade_close'
              ? 'PROFIT · bot sold'
              : 'BUY THIS SHARE · bot bought'}
          </div>
          <div className="ft-trade-alert-body">
            <strong>{(tradeAlert || board.lastAlert).stock}</strong>
            {(tradeAlert || board.lastAlert).price != null && (
              <span> @ ₹{(tradeAlert || board.lastAlert).price}</span>
            )}
            {(tradeAlert || board.lastAlert).qty != null && (
              <span> × {(tradeAlert || board.lastAlert).qty} sh</span>
            )}
            {(tradeAlert || board.lastAlert).net != null && (
              <span className="ft-trade-alert-net"> · net +₹{(tradeAlert || board.lastAlert).net}</span>
            )}
          </div>
          <div className="ft-trade-alert-hindi">
            {(tradeAlert || board.lastAlert).hindi || (tradeAlert || board.lastAlert).msg}
          </div>
          <div className="ft-trade-alert-actions">
            <button type="button" className="ft-btn" onClick={() => setTradeAlert(null)}>Dismiss</button>
            <span style={{ fontSize: '0.68rem', color: '#94a3b8' }}>
              Allow browser notifications when asked — so you get pinged even if tab is in background
            </span>
          </div>
        </div>
      )}

      {/* Automation toggle + profit report bar */}
      <div className="ft-pnl-bar" style={{ flexWrap: 'wrap', gap: 10 }}>
        {/* Automation on/off */}
        <div className="ft-pnl-item" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <button
            className={`ft-btn${automationOn ? ' success' : ''}`}
            style={{ minWidth: 160, fontWeight: 700, fontSize: '0.8rem',
              background: automationOn ? '#16a34a' : '#374151', color: '#fff',
              border: `2px solid ${automationOn ? '#4ade80' : '#6b7280'}` }}
            onClick={handleAutomation}
            disabled={automBusy}
          >
            {automBusy ? '…' : automationOn ? '🤖 AUTO ON' : '⏸ AUTO OFF'}
          </button>
          <span style={{ fontSize: '0.68rem', color: automationOn ? '#4ade80' : '#94a3b8', maxWidth: 140 }}>
            {automationOn
              ? 'Bot scans & trades every 60s'
              : 'Manual mode — bot paused'}
          </span>
        </div>

        <div className="ft-pnl-item">
          <span className="ft-pnl-label">Today's Net P&amp;L</span>
          <span className={`ft-pnl-value ${dailyPnl >= 0 ? 'pos' : 'neg'}`} style={{ fontSize: '1.25rem' }}>
            {dailyPnl >= 0 ? `+₹${dailyPnl.toFixed(2)}` : `−₹${Math.abs(dailyPnl).toFixed(2)}`}
          </span>
        </div>
        <div className="ft-pnl-item">
          <span className="ft-pnl-label">Total Profit (all days)</span>
          <span className={`ft-pnl-value ${totalPnl >= 0 ? 'pos' : 'neg'}`}>
            {totalPnl >= 0 ? `+₹${totalPnl.toFixed(2)}` : `−₹${Math.abs(totalPnl).toFixed(2)}`}
          </span>
        </div>
        <div className="ft-pnl-item">
          <span className="ft-pnl-label">Trades</span>
          <span className="ft-pnl-value">{totalTrades} <span style={{ fontSize: '0.7rem', color: '#4ade80' }}>✅{winTrades}</span></span>
        </div>
        {winRate != null && (
          <div className="ft-pnl-item">
            <span className="ft-pnl-label">Win Rate</span>
            <span className={`ft-pnl-value ${winRate >= 50 ? 'pos' : 'neg'}`}>{winRate}%</span>
          </div>
        )}
        <div className="ft-pnl-item">
          <span className="ft-pnl-label">Open Positions</span>
          <span className="ft-pnl-value">{positions.length} / {board?.maxPositions || 3}</span>
        </div>
        <div className="ft-pnl-item">
          <span className="ft-pnl-label">Filtered Stocks</span>
          <span className="ft-pnl-value">{filteredList.length}</span>
        </div>
        {board?.lastScanAt && (
          <div className="ft-pnl-item">
            <span className="ft-pnl-label">Last Scan</span>
            <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
              {new Date(board.lastScanAt).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          </div>
        )}
        <div style={{ marginLeft: 'auto' }}>
          <button className="ft-btn scan" onClick={handleScan} disabled={scanning}>
            {scanning ? (
              <span className="ft-scanning"><span className="ft-scanning-dot" /> Scanning…</span>
            ) : '🔍 Scan Now'}
          </button>
        </div>
      </div>

      {/* Settings */}
      <div className="ft-card">
        <div className="ft-card-title">Capital & Settings</div>
        <div className="ft-settings">
          <label>
            Capital per trade (₹)
            <input
              type="number" min="100"
              value={capitalInput}
              onChange={e => setCapitalInput(e.target.value)}
            />
          </label>
          <label>
            Profit target (₹/share)
            <input
              type="number" min="0.1" step="0.25"
              value={targetInput}
              onChange={e => setTargetInput(e.target.value)}
            />
          </label>
          <button className="ft-btn primary" onClick={handleSettings}>Apply</button>
        </div>
        {/* Live charge preview */}
        <div className="ft-charges-row">
          <span>Example @ ₹200/sh × {Math.max(1, Math.floor(capital / 200))} qty:</span>
          {(() => {
            const q = Math.max(1, Math.floor(capital / 200));
            const { gross, charges, net } = calcNetProfit(200, 200 + profitTargetRs, q);
            return (
              <>
                <span>Gross <strong>{fmt(gross)}</strong></span>
                <span>Charges <strong>−₹{charges.toFixed(2)}</strong></span>
                <span className={`ft-charges-net ${net > 0 ? 'pos' : 'neg'}`}>
                  Net {net > 0 ? `+₹${net.toFixed(2)}` : `−₹${Math.abs(net).toFixed(2)}`}
                </span>
                <span style={{ color: '#64748b', fontSize: '0.7rem' }}>
                  (Zerodha Intraday · STT on sell · GST · Stamp)
                </span>
              </>
            );
          })()}
        </div>
      </div>

      {/* ML Model Factors (research table) */}
      <div className="ft-card">
        <div className="ft-card-title">
          ML Model Factors <span>Indian NSE + BSE only · scan every 60s</span>
        </div>
        <div className="ft-table-wrap">
          <table className="ft-table ft-ml-table">
            <thead>
              <tr>
                <th>Model / Approach</th>
                <th>Accuracy (approx.)</th>
                <th>Context (India)</th>
              </tr>
            </thead>
            <tbody>
              {(board?.mlFactors || [
                { name: 'Hybrid Ensemble (SVC+LR+RF+Voting)', accuracy: '95.8%', context: 'NSE Nifty / liquid large-caps — direction vote' },
                { name: 'Random Forest', accuracy: '91% (2% move) / 96% (4% move)', context: 'BSE pharmaceutical & midcaps — direction detection' },
                { name: 'LSTM', accuracy: '85.3%', context: 'Indian NSE/BSE stocks with technical indicators' },
                { name: 'LSTM + Sentiment (Hybrid)', accuracy: '~57%+', context: 'NSE/BSE blue-chips — momentum as India sentiment proxy' },
                { name: 'Neural Networks', accuracy: '85% (2% move) / 95% (4% move)', context: 'BSE pharmaceutical & Indian equity direction' }
              ]).map((f) => (
                <tr key={f.id || f.name}>
                  <td><strong>{f.name}</strong></td>
                  <td className="ft-ml-acc">{f.accuracy}</td>
                  <td style={{ color: '#94a3b8', fontSize: '0.78rem' }}>{f.context}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: 8 }}>
          Only NSE (.NS) &amp; BSE (.BO) · majority model UP (3/5) · sell only when net profit &gt; 0
        </div>
      </div>

      {/* BUY / HOLD / SELL workflow tabs */}
      <div className="ft-card">
        <div className="ft-card-title">
          Workflow <span>what to buy · hold · sell</span>
        </div>
        <div className="ft-tabs" role="tablist">
          {[
            { id: 'buy', label: 'BUY', count: buyTab.length, hint: 'ML picks ready to buy' },
            { id: 'hold', label: 'HOLD', count: holdTab.length, hint: 'Open · waiting net > 0' },
            { id: 'sell', label: 'SELL', count: sellTab.length, hint: 'Net profit · sell now' },
            { id: 'sold', label: 'Sold Today', count: soldTab.length, hint: 'Closed trades' }
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={activeTab === t.id}
              className={`ft-tab ft-tab-${t.id}${activeTab === t.id ? ' active' : ''}`}
              onClick={() => setActiveTab(t.id)}
              title={t.hint}
            >
              {t.label}
              <span className="ft-tab-count">{t.count}</span>
            </button>
          ))}
        </div>

        {activeTab === 'buy' && (
          <div className="ft-tab-panel">
            {buyTab.length === 0 ? (
              <div className="ft-empty">
                <strong>No BUY candidates yet</strong>
                Scan for ML UP stocks, or wait for the next 60s auto-scan.
              </div>
            ) : (
              <div className="ft-table-wrap">
                <table className="ft-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Stock</th>
                      <th>Price</th>
                      <th>Change</th>
                      <th>Score</th>
                      <th>Net @ target</th>
                      <th>Why</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {buyTab.map((row, i) => {
                      const q = Math.max(1, Math.floor(capital / (row.price || 1)));
                      const exitPx = round2(Number(row.price) + profitTargetRs);
                      const { net } = calcNetProfit(Number(row.price), exitPx, q);
                      return (
                        <tr key={row.symbol || row.stock}>
                          <td style={{ color: '#64748b' }}>{i + 1}</td>
                          <td>
                            <strong>{row.stock}</strong>
                            <br />
                            <span style={{ fontSize: '0.65rem', color: '#64748b' }}>{row.symbol}</span>
                          </td>
                          <td>₹{Number(row.price).toFixed(2)}</td>
                          <td className={`ft-change ${(row.changePct || 0) >= 0 ? 'pos' : 'neg'}`}>
                            {pct(row.changePct || 0)}
                          </td>
                          <td><span className={`ft-score ${row.score >= 60 ? 'high' : ''}`}>{row.score}</span></td>
                          <td className={net > 0 ? 'ft-net-ok' : 'ft-net-no'}>
                            {net > 0 ? `+₹${net.toFixed(2)}` : `−₹${Math.abs(net).toFixed(2)}`}
                          </td>
                          <td style={{ fontSize: '0.72rem', color: '#94a3b8', maxWidth: 220 }}>
                            {row.workflowReason || 'ML pass · ready to BUY'}
                          </td>
                          <td>
                            <button
                              className="ft-btn primary small"
                              onClick={() => setBuyRow(row)}
                              disabled={net <= 0}
                            >
                              BUY
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'hold' && (
          <div className="ft-tab-panel">
            {holdTab.length === 0 ? (
              <div className="ft-empty">
                <strong>Nothing on HOLD</strong>
                Open positions waiting for net profit &gt; 0 appear here.
              </div>
            ) : (
              <div className="ft-positions">{holdTab.map(renderPosCard)}</div>
            )}
          </div>
        )}

        {activeTab === 'sell' && (
          <div className="ft-tab-panel">
            {sellTab.length === 0 ? (
              <div className="ft-empty">
                <strong>Nothing ready to SELL</strong>
                When net profit &gt; 0, positions move here (auto-sell if AUTO ON).
              </div>
            ) : (
              <div className="ft-positions">{sellTab.map(renderPosCard)}</div>
            )}
          </div>
        )}

        {activeTab === 'sold' && (
          <div className="ft-tab-panel">
            {soldTab.length === 0 ? (
              <div className="ft-empty">
                <strong>No closed trades today</strong>
                Sold names land here after a profit exit.
              </div>
            ) : (
              <div className="ft-trade-list">
                {soldTab.map((t) => (
                  <div key={t.id || `${t.stock}-${t.exitAt}`} className="ft-trade-row">
                    <span className="ft-trade-stock">{t.stock}
                      {t.autoEntry && <span style={{ fontSize: '0.58rem', background: '#1e3a5f', color: '#93c5fd', borderRadius: 3, padding: '1px 4px', marginLeft: 5 }}>AUTO</span>}
                    </span>
                    <span>Entry ₹{t.entryPrice}</span>
                    <span>Exit ₹{t.exitPrice}</span>
                    <span>Qty {t.qty}</span>
                    <span>Charges ₹{round2(t.charges).toFixed(2)}</span>
                    <span className={`ft-trade-net ${t.net >= 0 ? 'pos' : 'neg'}`}>
                      Net {t.net >= 0 ? `+₹${t.net}` : `−₹${Math.abs(t.net)}`}
                    </span>
                    <span style={{ fontSize: '0.65rem', color: '#64748b', marginLeft: 'auto' }}>
                      {t.exitAt ? new Date(t.exitAt).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit' }) : ''}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Filtered stock list (full scan) */}
      <div className="ft-card">
        <div className="ft-card-title">
          Filtered Stocks <span>{filteredList.length} passed ML factors</span>
          {board && !board.lastScanAt && (
            <span style={{ marginLeft: 8, color: '#f59e0b', fontSize: '0.7rem' }}>
              ⏳ First scan running…
            </span>
          )}
        </div>
        {filteredList.length === 0 ? (
          <div className="ft-empty">
            <strong>No stocks passed ML factors yet</strong>
            {marketOpen
              ? 'Scan runs every 60s during market hours. Click "Scan Now" to force one.'
              : 'Market is closed — filtered list will update at next market open.'}
          </div>
        ) : (
          <div className="ft-table-wrap">
            <table className="ft-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Stock</th>
                  <th>Price</th>
                  <th>Change</th>
                  <th>Volume</th>
                  <th>Day Range%</th>
                  <th>RSI</th>
                  <th>Room ₹</th>
                  <th>Suggest Qty</th>
                  <th>Net @ target</th>
                  <th>Score</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredList.map((row, i) => {
                  const q = Math.max(1, Math.floor(capital / row.price));
                  const exitPx = round2(Number(row.price) + profitTargetRs);
                  const { net, charges } = calcNetProfit(Number(row.price), exitPx, q);
                  const netOk = net > 0;
                  const alreadyOpen = positions.some(p => p.stock === row.stock && !p.sold);
                  return (
                    <tr key={row.symbol}>
                      <td style={{ color: '#64748b' }}>{i + 1}</td>
                      <td><strong>{row.stock}</strong><br /><span style={{ fontSize: '0.65rem', color: '#64748b' }}>{row.symbol}</span></td>
                      <td>₹{row.price.toFixed(2)}</td>
                      <td className={`ft-change ${row.changePct >= 0 ? 'pos' : 'neg'}`}>{pct(row.changePct)}</td>
                      <td>{(row.volume / 1000).toFixed(0)}K</td>
                      <td>{row.dayRangePct?.toFixed(2)}%</td>
                      <td style={{ color: row.rsi > 65 ? '#f59e0b' : '#94a3b8' }}>{row.rsi || '—'}</td>
                      <td>₹{row.roomToHigh?.toFixed(2)}</td>
                      <td>{q}</td>
                      <td>
                        {netOk
                          ? <span className="ft-net-ok">+₹{net.toFixed(2)}</span>
                          : <span className="ft-net-no">−₹{Math.abs(net).toFixed(2)}</span>}
                        <span style={{ fontSize: '0.62rem', color: '#64748b', marginLeft: 4 }}>
                          (charges ₹{charges.toFixed(2)})
                        </span>
                      </td>
                      <td>
                        <span className={`ft-score ${row.score >= 60 ? 'high' : ''}`}>{row.score}</span>
                      </td>
                      <td>
                        {alreadyOpen ? (
                          <span style={{ fontSize: '0.68rem', color: '#64748b' }}>Position open</span>
                        ) : (
                          <button
                            className="ft-btn primary small"
                            onClick={() => setBuyRow(row)}
                            disabled={!netOk}
                            title={!netOk ? 'Net profit not possible at this price/qty — increase target or wait' : ''}
                          >
                            BUY
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Activity log */}
      <div className="ft-card">
        <div className="ft-card-title">Activity Log</div>
        {log.length === 0 ? (
          <div className="ft-empty"><strong>No activity yet</strong>Scan or buy a stock to see logs.</div>
        ) : (
          <div className="ft-log">
            {log.map((entry, i) => (
              <div
                key={i}
                className={`ft-log-row${entry.alert ? (entry.type === 'trade_close' ? ' alert-profit' : ' alert-buy') : ''}`}
              >
                <span className="ft-log-at">{entry.at}</span>
                <span>
                  {entry.alert && entry.type === 'trade_open' ? '🟢 BUY · ' : ''}
                  {entry.alert && entry.type === 'trade_close' ? '💰 PROFIT · ' : ''}
                  {entry.msg}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Buy modal */}
      {buyRow && (
        <BuyModal
          row={buyRow}
          capital={capital}
          profitTargetRs={profitTargetRs}
          onClose={() => setBuyRow(null)}
          onBuy={() => { loadBoard(); showToast(`📥 ${buyRow.stock} added to positions`); }}
        />
      )}
    </div>
  );
}
